import React, { useEffect, useRef, useState } from 'react';
import useVisitorAccentColor from '../hooks/useVisitorAccentColor';

function parseRgb(color) {
  if (!color) return null;
  const probe = document.createElement('span');
  probe.style.color = color;
  document.body.appendChild(probe);
  const rgb = window.getComputedStyle(probe).color;
  document.body.removeChild(probe);
  const parts = rgb.match(/[\d.]+/g);
  if (!parts || parts.length < 3) return null;
  return { r: Math.round(+parts[0]), g: Math.round(+parts[1]), b: Math.round(+parts[2]) };
}

function colorsMatch(a, b, tolerance = 14) {
  const ca = parseRgb(a);
  const cb = parseRgb(b);
  if (!ca || !cb) return false;
  return (
    Math.abs(ca.r - cb.r) <= tolerance &&
    Math.abs(ca.g - cb.g) <= tolerance &&
    Math.abs(ca.b - cb.b) <= tolerance
  );
}

function backgroundAtPoint(x, y) {
  const el = document.elementFromPoint(x, y);
  if (!el) return null;
  let node = el;
  while (node && node !== document.documentElement) {
    const { backgroundColor } = window.getComputedStyle(node);
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      return backgroundColor;
    }
    node = node.parentElement;
  }
  return null;
}

export default function CustomCursor() {
  const accentColor = useVisitorAccentColor();
  const accentRef = useRef(accentColor);
  const cursorRef = useRef(null);
  const [clicking, setClicking] = useState(false);
  const [visible, setVisible] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [cursorColor, setCursorColor] = useState(accentColor);
  const pos = useRef({ x: 0, y: 0 });
  const frame = useRef(null);

  useEffect(() => {
    accentRef.current = accentColor;
  }, [accentColor]);

  useEffect(() => {
    setEnabled(window.matchMedia('(pointer: fine)').matches);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    document.documentElement.classList.add('custom-cursor-active');

    const applyPosition = () => {
      if (!cursorRef.current) return;
      cursorRef.current.style.transform = `translate3d(${pos.current.x}px, ${pos.current.y}px, 0)`;
    };

    const onMove = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      const hide =
        e.target.closest('canvas') ||
        e.target.closest('[data-native-cursor]');
      setVisible(!hide);

      const bg = backgroundAtPoint(e.clientX, e.clientY);
      const onAccentBg = bg && colorsMatch(bg, accentRef.current);
      setCursorColor(onAccentBg ? '#ffffff' : accentRef.current);

      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = requestAnimationFrame(applyPosition);
    };

    const onDown = () => setClicking(true);
    const onUp = () => setClicking(false);
    const onLeave = () => setVisible(false);

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    document.documentElement.addEventListener('mouseleave', onLeave);

    return () => {
      document.documentElement.classList.remove('custom-cursor-active');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      document.documentElement.removeEventListener('mouseleave', onLeave);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div
      ref={cursorRef}
      className={`custom-cursor ${clicking ? 'is-clicking' : ''} ${visible ? 'is-visible' : ''}`}
      style={{ '--cursor-accent': cursorColor }}
      aria-hidden="true"
    >
      <span className="custom-cursor__ring" />
      <span className="custom-cursor__dot" />
    </div>
  );
}
